import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import {
  getDigitalTwinsOptions,
  useShareDigiTwin,
} from '@/features/estate-media/api/get-digital-twins';
import { ShareDigiTwinPopup } from '@/features/estate-media/components/modals/share-digi-twin-popup';

type DigitalTwinLinkOption = {
  name: string;
  token: string;
};

export function FlatfinderDigitalTwinLinkField({
  disabled,
  estateId,
  onChange,
  value,
}: {
  disabled: boolean;
  estateId: string | null;
  onChange: (token: string | null) => void;
  value: string | null;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [creationOpen, setCreationOpen] = useState(false);
  const [createdLink, setCreatedLink] = useState<DigitalTwinLinkOption | null>(null);
  const digitalTwinsQuery = useQuery({
    ...getDigitalTwinsOptions(estateId ?? ''),
    enabled: Boolean(estateId),
  });
  const digitalTwins = digitalTwinsQuery.data?.digitalTwins ?? [];
  const activeLinks = useMemo(() => {
    const links = digitalTwins.flatMap((digitalTwin) =>
      digitalTwin.roomPlans.flatMap((roomPlan) =>
        roomPlan.presignedUrls
          .filter(
            (link) =>
              !!link.expiryDate &&
              Date.parse(link.expiryDate) > Date.now() &&
              (link.additionalData?.editingMode === 'view' ||
                link.additionalData?.editingMode === 'limited')
          )
          .map((link) => ({
            name: link.name || t('unnamedSharedLink'),
            token: link.token,
          }))
      )
    );
    if (createdLink && !links.some((link) => link.token === createdLink.token)) {
      links.push(createdLink);
    }
    return links;
  }, [createdLink, digitalTwins, t]);

  const creationTarget = digitalTwins.find((digitalTwin) => digitalTwin.roomPlans[0]) ?? null;

  const { mutate: createLink, isPending: creatingLink } = useShareDigiTwin({
    mutationConfig: {
      onSuccess: ([link]) => {
        if (!link) return;
        const editingMode = link.additionalData?.editingMode;
        if (editingMode !== 'view' && editingMode !== 'limited') return;
        setCreatedLink({
          name: link.name || creationTarget?.name || t('digitalTwinLink'),
          token: link.token,
        });
        onChange(link.token);
        setCreationOpen(false);
        if (estateId) {
          void queryClient.invalidateQueries({
            queryKey: getDigitalTwinsOptions(estateId).queryKey,
          });
        }
        toast.success(t('digitalTwinLinkCreated'));
      },
      onError: (error) => toast.error(error.message),
    },
  });

  if (digitalTwins.length === 0) return null;

  if (activeLinks.length > 0) {
    return (
      <Select
        label={t('digitalTwinLink')}
        value={value ?? ''}
        onChange={({ target }) => onChange(target.value || null)}
        options={[
          { id: '', title: t('noDigitalTwinLink') },
          ...activeLinks.map((link) => ({
            id: link.token,
            title: link.name,
          })),
        ]}
        disabled={disabled}
        disableClear
        wrapperClassName="sm:col-span-2"
      />
    );
  }

  if (!creationTarget?.roomPlans[0] || !digitalTwinsQuery.data?.metadata.canUpdate) return null;

  return (
    <div className="sm:col-span-2">
      <p className="mb-2 text-sm text-text-primary">{t('digitalTwinLink')}</p>
      <ShareDigiTwinPopup
        mode="create"
        initialName={t('defaultSharedLinkName', { number: 1 })}
        pending={creatingLink}
        open={creationOpen}
        onOpen={() => setCreationOpen(true)}
        onCancel={() => setCreationOpen(false)}
        onShare={({ expiresIn, name, ...additionalData }) => {
          createLink({
            id: creationTarget.id,
            planId: creationTarget.roomPlans[0].id,
            expiresIn: Math.max(1, Number(expiresIn) || 1),
            name,
            additionalData,
          });
        }}
      >
        <Button
          type="button"
          variant="outline"
          disabled={disabled || creatingLink}
          onClick={() => setCreationOpen(true)}
        >
          {t('createShareLink')}
        </Button>
      </ShareDigiTwinPopup>
    </div>
  );
}
